import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { descargarPorLink } from '@lib/services/exportaciones.service';

/**
 * Página pública que recibe el link del mail de un export grande (VEN-595).
 *
 * El token NO viene en el path sino en el fragmento (`#`), a propósito: el
 * navegador no manda el fragmento al servidor, así que el token no aparece en
 * el log del `TraceabilityInterceptor`, ni en `url.full` de APM, ni en los
 * access logs de nginx. Acá se lee del hash y se manda por POST (que tampoco se
 * loguea) para traer el archivo.
 *
 * Por eso el link tampoco apunta al API: el API vive en otro host
 * (`services*.apps`) y el nginx de este front no lo proxea — un link directo
 * devolvía el index.html de la SPA con HTTP 200 y nadie bajaba nada.
 */
export function DescargaExportPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const [estado, setEstado] = useState<'bajando' | 'listo' | 'error'>('bajando');
  const [mensaje, setMensaje] = useState('');
  const yaCorrio = useRef(false);

  useEffect(() => {
    if (yaCorrio.current) return;
    yaCorrio.current = true;

    // `slice(1)` saca el "#". Se limpia del address bar apenas se lee, así el
    // token no queda en el historial ni se va en un Referer si el usuario
    // navega a otro lado desde acá.
    const token = window.location.hash.slice(1);
    window.history.replaceState(null, '', window.location.pathname);

    if (!jobId || !token) {
      setEstado('error');
      setMensaje('El link está incompleto. Copialo de nuevo desde el mail, entero.');
      return;
    }

    descargarPorLink(jobId, token)
      .then(() => {
        setEstado('listo');
      })
      .catch(() => {
        setEstado('error');
        setMensaje(
          'El link no es válido, ya se usó o venció. Los links son de un solo uso y duran 48 horas: ' +
            'volvé a generar el reporte desde el micrositio.',
        );
      });
  }, [jobId]);

  return (
    <main
      style={{
        minHeight: '100dvh',
        display: 'grid',
        placeItems: 'center',
        padding: 16,
      }}
    >
      <div className="card" style={{ width: 'min(460px, 100%)', textAlign: 'center' }}>
        <h1 className="heading-5" style={{ marginTop: 0 }}>
          Descarga de tu reporte
        </h1>

        {estado === 'bajando' && (
          <p className="text-element-mid" style={{ lineHeight: 1.55 }}>
            Bajando el archivo…
          </p>
        )}

        {estado === 'listo' && (
          <>
            <p className="status status-success" style={{ display: 'block' }}>
              ✓ Listo, el archivo se descargó
            </p>
            <p className="text-element-mid" style={{ lineHeight: 1.55 }}>
              Ya podés cerrar esta pestaña. Por seguridad el archivo se borró del servidor y este
              link no vuelve a funcionar.
            </p>
          </>
        )}

        {estado === 'error' && (
          <p className="status status-error" style={{ display: 'block', lineHeight: 1.55 }}>
            {mensaje}
          </p>
        )}
      </div>
    </main>
  );
}
